// ============================================================================
// KLINISOL CRO - Progress Sidebar Component (REDESIGNED)
// Pure Tailwind CSS - Matches Dashboard/Navbar design system
// ============================================================================

import React from 'react';
import { 
    CheckCircle, 
    Circle, 
    Lock, 
    Play, 
    BookOpen, 
    FileQuestion, 
    Target 
} from 'lucide-react';

const ProgressSidebar = ({ sections, currentIndex, onNavigate }) => {
    // Calculate overall progress
    const completedCount = sections.filter(s => s.user_progress?.status === 'completed').length;
    const totalCount = sections.length;
    const progressPercentage = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
    
    // Calculate estimated time remaining
    const timeRemaining = sections
        .filter(s => s.user_progress?.status !== 'completed')
        .reduce((sum, s) => sum + (s.duration_minutes || 0), 0);
    
    // Get section icon based on type
    const getSectionIcon = (type) => {
        const iconClass = "w-5 h-5";
        const icons = {
            'lesson': <BookOpen className={iconClass} />,
            'quiz': <FileQuestion className={iconClass} />,
            'scenario': <Target className={iconClass} />
        };
        return icons[type] || icons['lesson'];
    };
    
    // Get status icon and styling
    const getStatusDisplay = (section, index) => {
        const status = section.user_progress?.status || 'not_started';
        const locked = isLocked(index);
        
        if (status === 'completed') {
            return {
                icon: <CheckCircle className="w-6 h-6 text-green-600 fill-green-100" />,
                color: 'text-green-600'
            };
        } else if (status === 'in_progress') {
            return {
                icon: <Play className="w-6 h-6 text-orange-500 fill-orange-100" />,
                color: 'text-orange-500'
            };
        } else if (locked) {
            return {
                icon: <Lock className="w-6 h-6 text-gray-400" />,
                color: 'text-gray-400'
            };
        } else {
            return {
                icon: <Circle className="w-6 h-6 text-gray-300" />,
                color: 'text-gray-300'
            };
        }
    };
    
    // Check if section is locked (linear progression)
    const isLocked = (index) => {
        if (index === 0) return false;
        
        for (let i = 0; i < index; i++) {
            const prevSection = sections[i];
            if (prevSection.is_required && prevSection.user_progress?.status !== 'completed') {
                return true;
            }
        }
        return false;
    };
    
    // Handle section click
    const handleSectionClick = (index) => {
        if (isLocked(index)) {
            alert('Please complete previous sections first');
            return;
        }
        onNavigate(index);
    };
    
    // Format time
    const formatTime = (minutes) => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        
        if (hours > 0) {
            return `${hours}h ${mins}m`;
        }
        return `${mins}m`;
    };

    return (
        <aside className="w-80 bg-white border-r border-gray-200 flex flex-col h-full overflow-hidden">
            {/* Progress Header */}
            <div className="p-6 border-b-2 border-gray-200 bg-gradient-to-br from-[#f8fafc] to-[#f1f5f9]">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                    Your Progress
                </h3>
                
                {/* Progress Bar */}
                <div className="mb-4">
                    <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden shadow-inner">
                        <div 
                            className="h-full bg-gradient-to-r from-[#475569] to-[#334155] rounded-full transition-all duration-500 ease-out"
                            style={{ width: `${progressPercentage}%` }}
                        />
                    </div>
                    <div className="text-center mt-2 text-2xl font-bold text-[#475569]">
                        {Math.round(progressPercentage)}%
                    </div>
                </div>
                
                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white p-3 rounded-lg border-2 border-gray-200 text-center">
                        <div className="text-xs font-semibold text-gray-500 uppercase mb-1">
                            Completed
                        </div>
                        <div className="text-2xl font-bold text-green-600">
                            {completedCount}
                        </div>
                        <div className="text-xs text-gray-500">
                            of {totalCount}
                        </div>
                    </div>
                    
                    {timeRemaining > 0 && (
                        <div className="bg-white p-3 rounded-lg border-2 border-gray-200 text-center">
                            <div className="text-xs font-semibold text-gray-500 uppercase mb-1">
                                Time Left
                            </div>
                            <div className="text-2xl font-bold text-[#475569]">
                                {formatTime(timeRemaining)}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            
            {/* Section List */}
            <nav className="flex-1 overflow-y-auto py-2">
                <h4 className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">
                    Sections
                </h4>
                
                {sections.map((section, index) => {
                    const isCurrent = index === currentIndex;
                    const status = section.user_progress?.status || 'not_started';
                    const locked = isLocked(index);
                    const statusDisplay = getStatusDisplay(section, index);
                    
                    return (
                        <div
                            key={section.id}
                            onClick={() => handleSectionClick(index)}
                            className={`
                                flex items-center px-6 py-4 gap-3 border-l-4 transition-all cursor-pointer
                                ${isCurrent 
                                    ? 'border-[#475569] bg-gradient-to-r from-gray-100 to-gray-200' 
                                    : status === 'completed'
                                    ? 'border-transparent bg-green-50 hover:bg-green-100'
                                    : 'border-transparent hover:bg-gray-50'
                                }
                                ${locked ? 'opacity-50 cursor-not-allowed' : ''}
                            `}
                        >
                            {/* Section Number Badge */}
                            <div className={`
                                flex items-center justify-center w-8 h-8 rounded-lg font-bold text-sm shadow-sm
                                ${isCurrent 
                                    ? 'bg-gradient-to-br from-[#475569] to-[#334155] text-white' 
                                    : status === 'completed'
                                    ? 'bg-gradient-to-br from-green-500 to-green-600 text-white'
                                    : 'bg-gray-200 text-gray-600'
                                }
                            `}>
                                {section.section_order}
                            </div>
                            
                            {/* Section Type Icon */}
                            <div className={statusDisplay.color}>
                                {getSectionIcon(section.section_type)}
                            </div>
                            
                            {/* Section Info */}
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold text-gray-900 text-sm mb-1 truncate">
                                    {section.title}
                                </div>
                                <div className="flex items-center gap-2 text-xs text-gray-600">
                                    <span className="bg-gray-100 px-2 py-0.5 rounded font-semibold">
                                        {section.duration_minutes}min
                                    </span>
                                    {section.is_required && (
                                        <span className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded font-semibold">
                                            Required
                                        </span>
                                    )}
                                </div>
                            </div>
                            
                            {/* Status Icon */}
                            <div className="flex-shrink-0">
                                {statusDisplay.icon}
                            </div>
                        </div>
                    );
                })}
            </nav>
            
            {/* Legend */}
            <div className="p-6 border-t-2 border-gray-200 bg-gradient-to-br from-[#f8fafc] to-[#f1f5f9]">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">
                    Legend
                </h4>
                <div className="grid grid-cols-2 gap-2.5">
                    <div className="flex items-center gap-2 text-xs font-medium text-gray-700">
                        <CheckCircle className="w-5 h-5 text-green-600 fill-green-100" />
                        <span>Completed</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs font-medium text-gray-700">
                        <Play className="w-5 h-5 text-orange-500 fill-orange-100" />
                        <span>In Progress</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs font-medium text-gray-700">
                        <Lock className="w-5 h-5 text-gray-400" />
                        <span>Locked</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs font-medium text-gray-700">
                        <Circle className="w-5 h-5 text-gray-300" />
                        <span>Not Started</span>
                    </div>
                </div>
            </div>
        </aside>
    );
};

export default ProgressSidebar;